// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Risk level scoring and classification.
 */

/**
 * Risk level thresholds for score classification.
 */
export const RISK_THRESHOLDS = {
  HIGH: 4,
  MEDIUM: 2,
} as const

/**
 * Risk level classification.
 */
export type RiskLevel = 'high' | 'medium' | 'low' | 'unknown'

/**
 * Tailwind class configuration for risk levels.
 */
export const RISK_COLORS: Record<RiskLevel, { bg: string; border: string; text: string; dot: string }> = {
  high: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    dot: '#ef4444',
  },
  medium: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: '#f59e0b',
  },
  low: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    dot: '#22c55e',
  },
  unknown: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    dot: '#94a3b8',
  },
}

/**
 * Get the risk level from a numeric score.
 * @param score - Risk score (1-5 scale)
 */
export function getRiskLevel(score: number | null | undefined): RiskLevel {
  if (score === null || score === undefined) return 'unknown'
  if (score >= RISK_THRESHOLDS.HIGH) return 'high'
  if (score >= RISK_THRESHOLDS.MEDIUM) return 'medium'
  return 'low'
}

/**
 * Get display label for a risk level.
 */
export function getRiskLabel(level: RiskLevel): string {
  const labels: Record<RiskLevel, string> = {
    high: 'High Risk',
    medium: 'Medium Risk',
    low: 'Low Risk',
    unknown: 'Unknown',
  }
  return labels[level]
}

/**
 * Get Tailwind classes for a risk level badge.
 */
export function getRiskBadgeClasses(level: RiskLevel): string {
  const colors = RISK_COLORS[level]
  return `${colors.bg} ${colors.border} ${colors.text}`
}

/**
 * Get Tailwind background class for risk indicator bars.
 */
export function getRiskBarClass(level: RiskLevel): string {
  const barColors: Record<RiskLevel, string> = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-green-500',
    unknown: 'bg-slate-400',
  }
  return barColors[level]
}

/**
 * Count items by risk level (high/medium/low) based on riskScore.
 */
export function computeRiskCounts<T extends { riskScore?: number | null }>(
  items: T[]
): { high: number; medium: number; low: number } {
  const counts = { high: 0, medium: 0, low: 0 }

  items.forEach(item => {
    const level = getRiskLevel(item.riskScore)
    if (level !== 'unknown') {
      counts[level]++
    }
  })

  return counts
}

/**
 * Sort findings by page number (ascending), then by risk score (descending).
 * Findings without page numbers sort to the end.
 */
export function sortByPageAndRisk<T extends { pageNumber?: number | null; riskScore?: number | null }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const pageA = a.pageNumber ?? Number.MAX_SAFE_INTEGER
    const pageB = b.pageNumber ?? Number.MAX_SAFE_INTEGER
    if (pageA !== pageB) return pageA - pageB
    return (b.riskScore ?? 0) - (a.riskScore ?? 0)
  })
}

/**
 * Sort items by page number (ascending). Items without page numbers sort to the end.
 */
export function sortByPageNumber<T extends { pageNumber?: number | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const pageA = a.pageNumber ?? Number.MAX_SAFE_INTEGER
    const pageB = b.pageNumber ?? Number.MAX_SAFE_INTEGER
    return pageA - pageB
  })
}
